import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FiHome, FiFolder, FiFile, FiMessageSquare, FiDownload, FiSearch, FiX, FiUploadCloud, FiChevronLeft, FiChevronRight, FiEye } from "react-icons/fi";
import { FaFilePdf, FaFileWord, FaFileExcel, FaFileImage, FaFile, FaVideo, FaClock } from "react-icons/fa";
import { IoMdNotificationsOutline } from "react-icons/io";
import { useTheme } from "../../context/SocketContext";
import FilePreviewModal from "../Tax Filling/FilePreviewModal";

const UserDocumentsPage = () => {
  const { currentThemeColors } = useTheme();
  const [activeTab, setActiveTab] = useState("main");
  const [documents, setDocuments] = useState([]);
  const [chatDocuments, setChatDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderPath, setFolderPath] = useState([]);
  const [filteredDocs, setFilteredDocs] = useState([]);
  const [filteredChatDocs, setFilteredChatDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [previewFile, setPreviewFile] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const sortDocumentsByDate = (docs) => {
    return [...docs].sort((a, b) => {
      const dateA = new Date(a.date || a.timestamp);
      const dateB = new Date(b.date || b.timestamp);
      return dateB - dateA;
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes || isNaN(bytes)) return "-";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(2)} KB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(2)} MB`;
    const gb = mb / 1024;
    return `${gb.toFixed(2)} GB`;
  };

  const buildDownloadUrl = (url) => {
    if (!url) return "#";
    let final = url.trim();
    // Clean legacy localhost prefix in stored URL
    final = final.replace(/http:\/\/localhost:5000\//, "/");
    // Clean legacy 'undefined' prefix in stored URL
    final = final.replace(/^undefined\/?/, "/");
    const isAbsolute = /^https?:\/\//i.test(final);
    if (isAbsolute) return final;
    const base = process.env.REACT_APP_API_BASE_URL || "";
    if (final.startsWith("/")) return `${base}${final}`;
    // Fallback: treat as relative uploads path
    return `${base}/${final}`;
  };

  const isNewDocument = (docDate) => {
    const docTime = new Date(docDate).getTime();
    const weekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    return docTime > weekAgo;
  };

  useEffect(() => {
    // Safety check to unlock body scroll that might be stuck from modals
    document.body.style.overflow = "";
    
    const fetchDocuments = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error("Failed to fetch user data");

        const data = await response.json();
        const sortedDocs = sortDocumentsByDate((data.user.documents || []).filter(doc => !doc.isDraft));
        setDocuments(sortedDocs);
        setFilteredDocs(sortedDocs);
        setFolders(data.user.folders || []);

        // Fetch active chat documents
        const chatResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/chat/documents?userId=${data.user._id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const activeChatDocs = chatResponse.ok ? await chatResponse.json() : [];

        // Fetch archived years
        const yearsResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/chat/available-years?userId=${data.user._id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        
        let archivedChatDocs = [];
        if (yearsResponse.ok) {
          const years = await yearsResponse.json();
          
          // Fetch archived documents for each year
          for (const yearData of years) {
            const archivedDocsResponse = await fetch(
              `${process.env.REACT_APP_API_BASE_URL}/api/chat/archived-documents?userId=${data.user._id}&year=${yearData.year}`,
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            if (archivedDocsResponse.ok) {
              const yearDocs = await archivedDocsResponse.json();
              archivedChatDocs = [...archivedChatDocs, ...yearDocs];
            }
          }
        }

        // Combine active and archived documents
        const allChatDocs = [...activeChatDocs, ...archivedChatDocs];
        const sortedChatDocs = sortDocumentsByDate(allChatDocs);
        setChatDocuments(sortedChatDocs);
        setFilteredChatDocs(sortedChatDocs);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
    const interval = setInterval(fetchDocuments, 30000);
    return () => clearInterval(interval);
  }, [navigate]);

  useEffect(() => {
    if (location.state?.openFolder && folders.length > 0) {
      const folder = folders.find(f => f.name === location.state.openFolder);
      if (folder) {
        setCurrentFolder(folder._id);
        setFolderPath([folder]);
        // Clear state to avoid re-opening on refresh
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [location.state, folders, navigate, location.pathname]);

  useEffect(() => {
    if (activeTab === "main") {
      const filtered = documents.filter(doc =>
        (currentFolder ? doc.folderId === currentFolder : !doc.folderId)
      );
      setFilteredDocs(filtered);
    } else {
      const filtered = chatDocuments.filter(doc =>
        true
      );
      setFilteredChatDocs(filtered);
    }
    setPage(1);
  }, [activeTab, documents, chatDocuments, currentFolder]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const openFolder = (folder) => {
    setCurrentFolder(folder._id);
    setFolderPath([...folderPath, folder]);
  };

  const navigateToFolder = (index) => {
    if (index === -1) {
      setCurrentFolder(null);
      setFolderPath([]);
    } else {
      const newPath = folderPath.slice(0, index + 1);
      setFolderPath(newPath);
      setCurrentFolder(newPath[newPath.length - 1]._id);
    }
  };

  const getCurrentFolderContents = () => {
    if (!currentFolder) return folders.filter(f => !f.parentFolder);
    return folders.filter(f => f.parentFolder === currentFolder);
  };

  const currentDocs = activeTab === "main" ? filteredDocs : filteredChatDocs;
  const count = Math.ceil(currentDocs.length / rowsPerPage);
  const paginatedDocs = currentDocs.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const getFileIcon = (fileName, isMeetingRecording) => {
    if (isMeetingRecording) {
      return <FaVideo className="w-6 h-6 text-purple-600" />;
    }
    const ext = fileName?.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return <FaFilePdf className="w-6 h-6 text-red-500" />;
      case 'doc':
      case 'docx': return <FaFileWord className="w-6 h-6 text-blue-500" />;
      case 'xls':
      case 'xlsx': return <FaFileExcel className="w-6 h-6 text-green-500" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return <FaFileImage className="w-6 h-6 text-purple-500" />;
      case 'mp4':
      case 'webm':
      case 'avi':
      case 'mov': return <FaVideo className="w-6 h-6 text-purple-600" />;
      default: return <FaFile className="w-6 h-6 text-gray-500" />;
    }
  };

  const getExpiryInfo = (expiresAt) => {
    if (!expiresAt) return null;
    const now = new Date();
    const expiry = new Date(expiresAt);
    const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    
    if (daysLeft < 0) return { text: 'Expired', color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-50 dark:bg-red-900/20' };
    if (daysLeft <= 7) return { text: `${daysLeft} days left`, color: 'text-orange-600 dark:text-orange-400', bgColor: 'bg-orange-50 dark:bg-orange-900/20' };
    if (daysLeft <= 30) return { text: `${daysLeft} days left`, color: 'text-yellow-600 dark:text-yellow-400', bgColor: 'bg-yellow-50 dark:bg-yellow-900/20' };
    return { text: `${daysLeft} days left`, color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-50 dark:bg-green-900/20' };
  };

  const renderFolders = () => {
    const folderContents = getCurrentFolderContents();
    if (folderContents.length === 0) return null;

    return (
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <FiFolder className="w-5 h-5 text-primary-500" />
          <h3 className="text-lg font-semibold text-dark-800 dark:text-white">Folders</h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {folderContents.map((folder) => (
            <div 
              key={folder._id}
              onClick={() => openFolder(folder)}
              className="group cursor-pointer p-4 rounded-xl bg-white dark:bg-dark-800 border border-gray-100 dark:border-dark-700 hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-lg transition-all duration-200"
            >
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/30 dark:to-primary-800/30 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                  <FiFolder className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                </div>
                <span className="text-sm font-medium text-dark-700 dark:text-gray-300 text-center line-clamp-2">{folder.name}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderDesktopView = (docs) => (
    <>
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-dark-700 border-b border-gray-100 dark:border-dark-600">
                <th className="px-6 py-4 text-left text-sm font-semibold text-dark-600 dark:text-gray-300 uppercase tracking-wider">Document</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-dark-600 dark:text-gray-300 uppercase tracking-wider">Date</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-dark-600 dark:text-gray-300 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
              {docs.map((document, index) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-dark-700/50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-dark-700 flex items-center justify-center">
                          {getFileIcon(document.originalName, document.isMeetingRecording)}
                        </div>
                        {isNewDocument(document.date || document.timestamp) && (
                          <span className="absolute -top-1 -right-1 w-4 h-4 bg-secondary-500 rounded-full flex items-center justify-center">
                            <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                          </span>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-dark-800 dark:text-white line-clamp-1">{document.originalName}</p>
                          {document.isMeetingRecording && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-xs font-medium">
                              <FaVideo className="w-3 h-3" />
                              Meeting Recording
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-sm text-gray-500 dark:text-gray-400">{formatFileSize(document.size)}</p>
                          {document.isMeetingRecording && document.meetingTitle && (
                            <>
                              <span className="text-gray-400">•</span>
                              <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">{document.meetingTitle}</p>
                            </>
                          )}
                        </div>
                        {document.isMeetingRecording && document.expiresAt && (
                          <div className="flex items-center gap-1 mt-1">
                            <FaClock className="w-3 h-3 text-gray-400" />
                            <span className={`text-xs font-medium ${getExpiryInfo(document.expiresAt)?.color}`}>
                              Auto-deletes in {getExpiryInfo(document.expiresAt)?.text}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                    {new Date(document.date || document.timestamp).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setPreviewFile({
                          url: document.fileUrl,
                          name: document.originalName,
                          size: document.size,
                          type: document.originalName?.split('.').pop()?.toLowerCase() === 'pdf' ? 'application/pdf' : 'image/jpeg' // Fallback to image if not pdf, FilePreviewModal handles it
                        })}
                        className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
                        title="Preview"
                      >
                        <FiEye className="w-5 h-5" />
                      </button>
                      <a
                        href={buildDownloadUrl(document.fileUrl)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 font-medium text-sm hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-colors"
                      >
                        <FiDownload className="w-4 h-4" />
                        Download
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {docs.length === 0 && (
          <div className="py-16 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gray-100 dark:bg-dark-700 flex items-center justify-center">
              <FiFile className="w-8 h-8 text-gray-400 dark:text-gray-500" />
            </div>
            <p className="text-lg font-medium text-dark-700 dark:text-gray-300">No documents found</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Documents will appear here once uploaded</p>
          </div>
        )}
      </div>
      {docs.length > 0 && count > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Showing {(page - 1) * rowsPerPage + 1} to {Math.min(page * rowsPerPage, currentDocs.length)} of {currentDocs.length} documents
          </p>
          <div className="flex items-center gap-2">
            <button 
              disabled={page === 1}
              onClick={() => handlePageChange(page - 1)}
              className="p-2 rounded-lg bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FiChevronLeft className="w-5 h-5" />
            </button>
            {Array.from({ length: Math.min(count, 5) }, (_, i) => {
              let pageNum;
              if (count <= 5) pageNum = i + 1;
              else if (page <= 3) pageNum = i + 1;
              else if (page >= count - 2) pageNum = count - 4 + i;
              else pageNum = page - 2 + i;
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`w-10 h-10 rounded-lg font-medium text-sm transition-colors ${
                    page === pageNum
                      ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/30'
                      : 'bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-700'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button 
              disabled={page === count}
              onClick={() => handlePageChange(page + 1)}
              className="p-2 rounded-lg bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FiChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </>
  );

  const renderMobileView = (docs) => (
    <>
      <div className="space-y-3">
        {docs.map((document, index) => (
          <div key={index} className="card p-4">
            <div className="flex items-start gap-3">
              <div className="relative flex-shrink-0">
                <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-dark-700 flex items-center justify-center">
                  {getFileIcon(document.originalName, document.isMeetingRecording)}
                </div>
                {isNewDocument(document.date || document.timestamp) && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-secondary-500 rounded-full flex items-center justify-center">
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <p className="font-medium text-dark-800 dark:text-white line-clamp-2 text-sm">{document.originalName}</p>
                  {document.isMeetingRecording && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-xs font-medium">
                      <FaVideo className="w-3 h-3" />
                      Recording
                    </span>
                  )}
                </div>
                {document.isMeetingRecording && document.meetingTitle && (
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1 line-clamp-1">{document.meetingTitle}</p>
                )}
                <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
                  <span>{formatFileSize(document.size)}</span>
                  <span>•</span>
                  <span>
                    {new Date(document.date || document.timestamp).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                {document.isMeetingRecording && document.expiresAt && (
                  <div className="flex items-center gap-1 mt-1">
                    <FaClock className="w-3 h-3 text-gray-400" />
                    <span className={`text-xs font-medium ${getExpiryInfo(document.expiresAt)?.color}`}>
                      {getExpiryInfo(document.expiresAt)?.text}
                    </span>
                  </div>
                )}
              </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => setPreviewFile({
                      url: document.fileUrl,
                      name: document.originalName,
                      size: document.size,
                      type: document.originalName?.split('.').pop()?.toLowerCase() === 'pdf' ? 'application/pdf' : 'image/jpeg'
                    })}
                    className="flex-shrink-0 w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
                  >
                    <FiEye className="w-5 h-5" />
                  </button>
                  <a
                    href={buildDownloadUrl(document.fileUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center text-primary-600 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-colors"
                  >
                    <FiDownload className="w-5 h-5" />
                  </a>
                </div>
            </div>
          </div>
        ))}
        {docs.length === 0 && (
          <div className="card py-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gray-100 dark:bg-dark-700 flex items-center justify-center">
              <FiFile className="w-8 h-8 text-gray-400 dark:text-gray-500" />
            </div>
            <p className="text-lg font-medium text-dark-700 dark:text-gray-300">No documents found</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {searchTerm ? 'Try a different search term' : 'Documents will appear here once uploaded'}
            </p>
          </div>
        )}
      </div>
      {docs.length > 0 && count > 1 && (
        <div className="flex items-center justify-between mt-6">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {Math.min(page * rowsPerPage, currentDocs.length)} of {currentDocs.length}
          </p>
          <div className="flex items-center gap-2">
            <button 
              disabled={page === 1}
              onClick={() => handlePageChange(page - 1)}
              className="p-2 rounded-lg bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 text-gray-600 dark:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FiChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-400">Page {page} of {count}</span>
            <button 
              disabled={page === count}
              onClick={() => handlePageChange(page + 1)}
              className="p-2 rounded-lg bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 text-gray-600 dark:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FiChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="card p-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '0ms', animationFillMode: 'forwards' }}>
        <h1 className="text-2xl font-bold text-dark-800 dark:text-white mb-2">
          <span style={{ color: 'var(--primary-500)' }}>Documents</span>
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          View and access all documents: Main Documents for files shared through checklists/steps and Chat documents for files exchanged via Chat.
        </p>
      </div>
      
      {/* Tab Buttons */}
      <div
        className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 opacity-0 animate-fade-in-up"
        style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}
      >
        <button 
          onClick={() => {
            setActiveTab('main');
            setCurrentFolder(null);
            setFolderPath([]);
          }}
          className={`w-full sm:w-auto flex items-center justify-center sm:justify-start gap-2 px-3 py-2 sm:px-5 sm:py-3 rounded-xl font-medium text-sm sm:text-base transition-all duration-300 hover:scale-105 ${
            activeTab === 'main'
              ? 'text-white shadow-lg'
              : 'bg-white dark:bg-dark-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-dark-700 hover:border-primary-300 dark:hover:border-primary-600'
          }`}
          style={activeTab === 'main' ? { 
            background: `linear-gradient(135deg, var(--primary-500), var(--primary-600))`,
            boxShadow: `0 10px 15px -3px var(--glow-color)`
          } : {}}
        >
          <FiUploadCloud className="w-5 h-5" />
          <span>Main Documents</span>
        </button>
        <button 
          onClick={() => {
            setActiveTab('chat');
            setCurrentFolder(null);
            setFolderPath([]);
          }}
          className={`w-full sm:w-auto flex items-center justify-center sm:justify-start gap-2 px-3 py-2 sm:px-5 sm:py-3 rounded-xl font-medium text-sm sm:text-base transition-all duration-300 hover:scale-105 ${
            activeTab === 'chat'
              ? 'text-white shadow-lg'
              : 'bg-white dark:bg-dark-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-dark-700 hover:border-primary-300 dark:hover:border-primary-600'
          }`}
          style={activeTab === 'chat' ? { 
            background: `linear-gradient(135deg, var(--primary-500), var(--primary-600))`,
            boxShadow: `0 10px 15px -3px var(--glow-color)`
          } : {}}
        >
          <FiMessageSquare className="w-5 h-5" />
          <span>Chat Documents</span>
        </button>
      </div>

      {/* Description */}
      <div className="banner-glass opacity-0 animate-fade-in-up" style={{ 
        animationDelay: '200ms', 
        animationFillMode: 'forwards'
      }}>
        <p className="font-medium">
          {activeTab === 'main' 
            ? 'Documents submitted or shared as part of each step (e.g., Initial Information, Draft Return, Acknowledgement) are saved here for easy reference.'
            : 'Documents shared by you or our team via the Chat box are stored here for your quick access.'
          }
        </p>
      </div>

      {/* Folder Navigation - Only show for Main Documents tab */}
      {activeTab === 'main' && (
        <div className="space-y-4">
          {/* Breadcrumbs */}
          <div className="flex items-center gap-3 flex-wrap">
            <button
              disabled={folderPath.length === 0}
              onClick={() => navigateToFolder(folderPath.length <= 1 ? -1 : folderPath.length - 2)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 text-gray-600 dark:text-gray-400 font-medium text-sm hover:bg-gray-50 dark:hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FiChevronLeft className="w-4 h-4" />
              Back
            </button>
            <div className="flex items-center gap-1 text-sm">
              <button 
                onClick={() => navigateToFolder(-1)}
                className="flex items-center gap-1 px-2 py-1 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
              >
                <FiHome className="w-4 h-4" />
                <span>Home</span>
              </button>
              {folderPath.map((f, i) => (
                <div key={f._id} className="flex items-center">
                  <span className="text-gray-400 dark:text-gray-500 mx-1">/</span>
                  <button 
                    onClick={() => navigateToFolder(i)}
                    className="px-2 py-1 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
                  >
                    {f.name}
                  </button>
                </div>
              ))}
            </div>
          </div>
          {renderFolders()}
        </div>
      )}

      {/* Documents List */}
      <div>
        {loading ? (
          <div className="card py-16 flex flex-col items-center justify-center">
            <div className="w-12 h-12 border-4 border-primary-200 dark:border-primary-800 border-t-primary-500 rounded-full animate-spin mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading documents...</p>
          </div>
        ) : (
          <>
            <div className="hidden md:block">
              {renderDesktopView(paginatedDocs)}
            </div>
            <div className="md:hidden">
              {renderMobileView(paginatedDocs)}
            </div>
          </>
        )}
      </div>

      {previewFile && (
        <FilePreviewModal
          file={previewFile}
          onClose={() => setPreviewFile(null)}
        />
      )}
    </div>
  );
};

export default UserDocumentsPage;